import React from "react";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const FAQ_ITEMS = [
  {
    q: "Puis-je réserver une demi-journée ?",
    a: (
      <>Oui. Vous pouvez réserver votre espace à la matinée ou à l'après-midi, selon les disponibilités affichées dans le calendrier. Si une demi-journée est déjà réservée, l'autre créneau reste disponible.</>
    ),
  },
  {
    q: "Puis-je réserver plusieurs jours ?",
    a: <>Oui. Vous pouvez réserver un espace pour plusieurs jours consécutifs, sous réserve des disponibilités. Il suffit de sélectionner la période souhaitée lors de votre réservation.</>,
  },
  {
    q: "Puis-je venir sans abonnement ?",
    a: <>Oui. Aucun abonnement n'est obligatoire. Vous pouvez utiliser Synergie Workspace ponctuellement et choisir la formule correspondant à vos besoins : demi-journée, journée ou plusieurs jours, selon l'espace sélectionné.</>,
  },
  {
    q: "Peut-on recevoir des clients ou des partenaires ?",
    a: <>Oui. Pour vos rendez-vous professionnels, vous pouvez réserver le <strong>bureau privatif / salle d'entretien</strong> ou la <strong>salle de réunion</strong>, selon le nombre de personnes et la configuration dont vous avez besoin.</>,
  },
  {
    q: "Quels sont les horaires d'accès ?",
    a: <>Synergie Workspace est accessible <strong>7 jours sur 7, de 7h à 20h</strong>, pour vous permettre de travailler selon votre organisation et vos besoins.</>,
  },
  {
    q: "Où peut-on se garer ?",
    a: <>
      Plusieurs parkings publics gratuits se trouvent à quelques minutes à pied de Synergie Workspace :
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li><strong>Parking José Cardona</strong> — à 2 min à pied (16 places)</li>
        <li><strong>Parking Dr Jacques Arnoux</strong> — à 3 min à pied (24 places)</li>
        <li><strong>Parking Carrefour Market Vauvert</strong> — à 5 min à pied</li>
        <li><strong>Parking Raymond Erb</strong> — à 7 min à pied (110 places)</li>
      </ul>
    </>,
  },
  {
    q: "Comment modifier ou annuler une réservation ?",
    a: (
      <>
        Une réservation peut être modifiée ou annulée conformément aux conditions de réservation et d'annulation applicables. Pour toute demande,{" "}
        <Link to="/contact" className="text-[#2B8B8E] underline">contactez Synergie Workspace</Link>{" "}
        en précisant les informations de votre réservation.
      </>
    ),
  },
  {
    q: "Une facture est-elle fournie ?",
    a: <>Oui. Une facture est établie pour votre réservation et vous est remise avec les documents contractuels correspondants. Vous pouvez la retrouver dans votre <Link to="/mon-compte" className="text-[#2B8B8E] underline">espace client</Link>.</>,
  },
  {
    q: "Le café est-il inclus ?",
    a: (
      <>
        Le café est inclus pour les utilisateurs du <strong>Workspace / Open Space</strong>, selon les conditions de la formule réservée.<br />
        Pour le <strong>bureau privatif / salle d'entretien</strong> et la <strong>salle de réunion</strong>, les prestations incluses sont celles indiquées dans la formule choisie au moment de la réservation.
      </>
    ),
  },
  {
    q: "La connexion Internet est-elle incluse ?",
    a: <>Oui, l'accès à Internet est inclus lorsqu'il fait partie des équipements de l'espace réservé. Les équipements et services compris sont clairement indiqués dans le descriptif de chaque espace avant la réservation.</>,
  },
  {
    q: "Quels espaces puis-je réserver ?",
    a: (
      <>
        Synergie Workspace propose trois espaces principaux :
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li><strong>Workspace / Open Space</strong> pour travailler dans un espace partagé et convivial ;</li>
          <li><strong>Bureau privatif / Salle d'entretien</strong> pour travailler au calme ou recevoir une personne en toute confidentialité ;</li>
          <li><strong>Salle de réunion</strong> pour vos réunions, formations, visioconférences et rendez-vous professionnels.</li>
        </ul>
      </>
    ),
  },
  {
    q: "Comment savoir si un espace est disponible ?",
    a: (
      <>
        Les disponibilités sont affichées directement dans le calendrier de réservation. Une journée peut être totalement disponible, partiellement occupée ou complète.
        <div className="mt-2 p-3 rounded-lg bg-muted/50 text-xs">
          <strong>Légende du calendrier :</strong>{" "}
          <span className="inline-block bg-[#E0B66A] text-[#0D2C4D] px-1 rounded font-bold text-[10px] mx-0.5">AM</span> matinée réservée ·{" "}
          <span className="inline-block bg-[#0D2C4D] text-white px-1 rounded font-bold text-[10px] mx-0.5">PM</span> après-midi réservé ·{" "}
          Date grisée / barrée = journée complète.
        </div>
        Ainsi, lorsqu'une matinée est déjà réservée, vous pouvez toujours réserver l'après-midi si celui-ci reste disponible.
      </>
    ),
  },
  {
    q: "Comment réserver un espace ?",
    a: (
      <>
        La réservation s'effectue directement en ligne :
        <ol className="list-decimal pl-5 mt-2 space-y-1">
          <li>Choisissez votre espace.</li>
          <li>Sélectionnez votre date ou votre période.</li>
          <li>Choisissez votre créneau disponible.</li>
          <li>Vérifiez le récapitulatif et le tarif.</li>
          <li>Confirmez votre réservation.</li>
        </ol>
      </>
    ),
  },
  {
    q: "Vais-je recevoir une confirmation ?",
    a: <>Oui. Après validation de votre réservation, vous recevez une confirmation récapitulant l'espace réservé, la date, le créneau et les informations utiles.</>,
  },
  {
    q: "Les équipements sont-ils compris dans le tarif ?",
    a: <>Les équipements et services inclus dépendent de l'espace et de la formule réservés. Ils sont précisés dans le descriptif de chaque espace avant la réservation afin que vous sachiez exactement ce qui est compris dans votre réservation.</>,
  },
];

export default function FAQ() {
  return (
    <div className="container-x py-16 max-w-4xl" data-testid="faq-page">
      <p className="overline text-[#2B8B8E]">FAQ</p>
      <h1 className="text-4xl sm:text-5xl font-light mt-2 mb-3">Questions fréquentes.</h1>
      <p className="text-muted-foreground mb-10">Tout ce que vous devez savoir avant de réserver.</p>

      <Accordion type="single" collapsible className="w-full">
        {FAQ_ITEMS.map((item, i) => (
          <AccordionItem key={item.q} value={`item-${i}`} data-testid={`faq-item-${i}`}>
            <AccordionTrigger className="text-left text-base font-medium hover:text-[#2B8B8E]">
              {item.q}
            </AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
              {item.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <div className="mt-12 p-6 rounded-2xl bg-[#2B8B8E]/5 border border-[#2B8B8E]/20 text-center">
        <p className="text-sm text-muted-foreground mb-3">Vous ne trouvez pas la réponse à votre question ?</p>
        <Link to="/contact" className="inline-block px-6 py-2 rounded-full bg-[#2B8B8E] hover:bg-[#1F6F73] text-white font-semibold text-sm">
          Nous contacter
        </Link>
      </div>
    </div>
  );
}
